import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import firebaseApp from "../firebase/credenciales";

const db = getFirestore(firebaseApp);

/**
 * Obtiene todas las ocurrencias de un período específico
 * @param {string} periodo - Formato 'YYYY-MM'
 * @returns {Promise<Object>} - Objeto con ocurrencias agrupadas por empleado
 */
export const obtenerOcurrenciasPeriodo = async (periodo) => {
  try {
    // Consultar colección de ocurrencias por período
    const ocurrenciasRef = collection(db, "ocurrencias");
    const q = query(ocurrenciasRef, where("periodo", "==", periodo));
    const ocurrenciasSnapshot = await getDocs(q);

    // Organizar ocurrencias por empleado
    const ocurrenciasPorEmpleado = {};
    
    ocurrenciasSnapshot.forEach(doc => {
      const data = doc.data();
      // Si hay un empleado_id en los datos
      if (data.empleado_id) {
        // Inicializar array si no existe
        if (!ocurrenciasPorEmpleado[data.empleado_id]) {
          ocurrenciasPorEmpleado[data.empleado_id] = [];
        }
        
        // Añadir la ocurrencia al array del empleado
        ocurrenciasPorEmpleado[data.empleado_id].push({
          id: doc.id,
          ...data
        });
      }
    });

    return ocurrenciasPorEmpleado;
  } catch (error) {
    console.error("Error al obtener ocurrencias:", error);
    throw error;
  }
};

/**
 * Guarda las ocurrencias de un empleado para un período específico
 * @param {string} empleadoId - ID del empleado
 * @param {string} periodo - Formato 'YYYY-MM'
 * @param {Array} ocurrencias - Lista de ocurrencias a guardar
 * @returns {Promise<void>}
 */
export const guardarOcurrencias = async (empleadoId, periodo, ocurrencias) => {
  try {
    // Primero, eliminar ocurrencias existentes para ese empleado y período
    await eliminarOcurrenciasEmpleadoPeriodo(empleadoId, periodo);
    
    // Luego, guardar las nuevas ocurrencias
    const batch = await Promise.all(ocurrencias.map(async (ocurrencia) => {
      const nuevaRef = doc(collection(db, "ocurrencias"));
      await setDoc(nuevaRef, {
        ...ocurrencia,
        empleado_id: empleadoId,
        periodo: periodo,
        fecha_creacion: new Date().toISOString()
      });
    }));
    
    return true;
  } catch (error) {
    console.error("Error al guardar ocurrencias:", error);
    throw error;
  }
};

/**
 * Elimina todas las ocurrencias de un empleado en un período específico
 * @param {string} empleadoId - ID del empleado
 * @param {string} periodo - Formato 'YYYY-MM'
 * @returns {Promise<void>}
 */
const eliminarOcurrenciasEmpleadoPeriodo = async (empleadoId, periodo) => {
  try {
    // Buscar todas las ocurrencias del empleado en ese período
    const ocurrenciasRef = collection(db, "ocurrencias");
    const q = query(
      ocurrenciasRef, 
      where("empleado_id", "==", empleadoId),
      where("periodo", "==", periodo)
    );
    const ocurrenciasSnapshot = await getDocs(q);
    
    // Eliminar cada documento
    const batch = await Promise.all(ocurrenciasSnapshot.docs.map(async (docSnapshot) => {
      await setDoc(doc(db, "ocurrencias", docSnapshot.id), { eliminado: true }, { merge: true });
    }));
    
    return true;
  } catch (error) {
    console.error("Error al eliminar ocurrencias:", error);
    throw error;
  }
};

/**
 * Obtiene todas las ocurrencias de un empleado
 * @param {string} empleadoId - ID del empleado
 * @returns {Promise<Array>} - Lista de ocurrencias del empleado
 */
export const obtenerOcurrenciasEmpleado = async (empleadoId) => {
  try {
    const ocurrenciasRef = collection(db, "ocurrencias");
    const q = query(
      ocurrenciasRef, 
      where("empleado_id", "==", empleadoId),
      where("eliminado", "!=", true)
    );
    const ocurrenciasSnapshot = await getDocs(q);
    
    const ocurrencias = [];
    ocurrenciasSnapshot.forEach(doc => {
      ocurrencias.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return ocurrencias;
  } catch (error) {
    console.error("Error al obtener ocurrencias del empleado:", error);
    throw error;
  }
};

/**
 * Obtiene todas las ocurrencias de un empleado para un período específico
 * @param {string} empleadoId - ID del empleado
 * @param {string} periodo - Formato 'YYYY-MM'
 * @returns {Promise<Array>} - Lista de ocurrencias del empleado en ese período
 */
export const obtenerOcurrenciasEmpleadoPeriodo = async (empleadoId, periodo) => {
  try {
    const ocurrenciasRef = collection(db, "ocurrencias");
    const q = query(
      ocurrenciasRef, 
      where("empleado_id", "==", empleadoId),
      where("periodo", "==", periodo),
      where("eliminado", "!=", true)
    );
    const ocurrenciasSnapshot = await getDocs(q);
    
    const ocurrencias = [];
    ocurrenciasSnapshot.forEach(doc => {
      ocurrencias.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return ocurrencias;
  } catch (error) {
    console.error("Error al obtener ocurrencias del empleado por período:", error);
    throw error;
  }
};